import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { useConfirmationWizard } from '@/features/Quote/hooks/useConfirmationWizard';
import { usePanelOpenClose } from '@/features/Quote/hooks/usePanelOpenClose';
import { detectUnconventionalConfig } from '@/features/Quote/services/coverageRules';
import { isFeatureActive } from '@/lib/featureFlags';
import type { CoverageValues, SettingType } from '@/types/app';
import { FeatureFlag } from '@/types/app';
import Alert from '../Alert';
import DetailCard from '../DetailCard';
import StepConfirmButton from '../StepConfirmButton';
import { DEFAULT_COVERAGE_VALUES, normalizeCoverageValues } from './constants';
import Coverage from './Details';

interface DetailsProps {
	activeSetting: SettingType | null;
	setActiveSetting: (setting: SettingType | null) => void;
	openedFromQuoteSelection?: boolean;
	confirmedSteps: ReadonlySet<SettingType>;
	markStepConfirmed: (step: SettingType) => void;
}

const Details = ({
	activeSetting,
	setActiveSetting,
	openedFromQuoteSelection = false,
	confirmedSteps,
	markStepConfirmed
}: DetailsProps) => {
	const { t } = useTranslation();
	const { config, appData, isSubmitting, premiumVarManagement } = useAppContext();
	const { getNextUnconfirmedStep } = useConfirmationWizard();
	// Next warning the user still has to confirm this session, or null when coverage
	// is the terminal step and confirming should recalculate everything at once.
	const nextStep = getNextUnconfirmedStep('coverage', confirmedSteps);

	// Coverage-related state - initialized with defaults, updated when panel opens
	const [coverageValues, setCoverageValues] = useState<CoverageValues>(DEFAULT_COVERAGE_VALUES);

	// Detect unconventional configurations
	const unconventionalDetection = useMemo(() => {
		try {
			return detectUnconventionalConfig(coverageValues);
		} catch (error) {
			console.error('Error detecting unconventional config:', error);
			return { hasIssues: false, triggeredRules: [], hasDowngrade: false, suggestedConfig: null };
		}
	}, [coverageValues]);

	// Coverage-related handlers
	const coverageInfo = useMemo(() => {
		// First check premium_variables.coverage which has the most up-to-date values after API updates
		const premiumVarsCoverage = appData?.premium_variables?.coverage?.sick_leave_short_term;
		if (premiumVarsCoverage) {
			return premiumVarsCoverage;
		}
		// Fall back to current_policy coverage if premium_variables.coverage is not available
		return appData?.current_policy?.sick_leave_short_term?.coverage || null;
	}, [
		appData?.premium_variables?.coverage?.sick_leave_short_term,
		appData?.current_policy?.sick_leave_short_term?.coverage
	]);

	const handleCoverageChange = useCallback(
		(values: CoverageValues) => {
			setCoverageValues(values);
			premiumVarManagement.coverage.updatePendingValues(values);
		},
		[premiumVarManagement.coverage]
	);

	// Track whether we kept the pending values intentionally (confirmed/advanced) so the
	// close effect below doesn't clear them; a genuine cancel leaves it false → discard.
	const justSavedRef = useRef(false);

	// Confirm coverage (optimistic green) and move on. Coverage values are kept pending
	// so the single deferred recalculation in Settings can include them. `target` is the
	// next unconfirmed step, or null when this was the last card (closing triggers the
	// commit).
	const confirmStep = useCallback(
		(target: SettingType | null) => {
			premiumVarManagement.coverage.updatePendingValues(coverageValues);
			markStepConfirmed('coverage');
			justSavedRef.current = true;
			setActiveSetting(target);
			window.scrollTo({ top: 0, behavior: 'smooth' });
		},
		[coverageValues, premiumVarManagement.coverage, markStepConfirmed, setActiveSetting]
	);

	// Handle panel open/close transitions: reset to saved values on open, and clear the
	// pending edits on cancel so the unconfirmed coverage is discarded.
	const handleOpen = useCallback(() => {
		if (coverageInfo && Object.keys(coverageInfo).length > 0) {
			const values = normalizeCoverageValues(coverageInfo);
			setCoverageValues(values);
			// Sync pending values so SummaryPanel comparison is accurate
			premiumVarManagement.coverage.updatePendingValues(values);
			justSavedRef.current = false;
		}
	}, [coverageInfo, premiumVarManagement.coverage]);

	const handleClose = useCallback(() => {
		// Clear pending values only when the panel closes WITHOUT saving (cancelled).
		if (!justSavedRef.current) {
			premiumVarManagement.coverage.updatePendingValues(null);
		}
		justSavedRef.current = false;
	}, [premiumVarManagement.coverage]);

	usePanelOpenClose(activeSetting === 'coverage', { onOpen: handleOpen, onClose: handleClose });

	return (
		<DetailCard
			title={t('quotes.settings.coverage.title')}
			dataAttribute="edit-coverage-card"
			type="coverage"
			isVisible={activeSetting === 'coverage'}
			onClose={() => setActiveSetting(null)}
		>
			{openedFromQuoteSelection && (
				<Alert
					variant="info"
					className="mb-4"
					testId="coverage-info-alert"
					showIcon
					text={t('quotes.settings.coverage.updateWarning')}
				/>
			)}
			<h6 className="font-bold text-lg mb-2">{t('quotes.settings.coverage.detailsTitle')}</h6>
			<Coverage
				coverageValues={coverageValues}
				onCoverageChange={handleCoverageChange}
				unconventionalDetection={unconventionalDetection}
			/>
			{isFeatureActive(config, FeatureFlag.ALLOW_USER_COVERAGE_OVERRIDE) && (
				<>
					<hr className="my-4 border-border" />
					<StepConfirmButton
						nextStep={nextStep}
						onConfirm={confirmStep}
						disabled={isSubmitting}
						loading={isSubmitting}
					/>
				</>
			)}
		</DetailCard>
	);
};

export default Details;
